//-- Copyright (c) 2021 TMT Insights Group, LLC
import { FileTypeGrouping, FileTypes, LoadingStatuses } from '@/constants/constants';
import { createSelector } from '@reduxjs/toolkit';
import { AssetType, ConstantsState, LanguageObject, TerritoryConstant } from '../slices/slice_types';
import { MediaResolutionsList } from '@/constants/mediaConstants';
import { RootState } from '../store';

export const selectConstants = (state: RootState) => state.constants || {};

export const selectConstantStatus = createSelector([selectConstants], (constants) => constants.status || {});
export const selectConstantsInitFinished = createSelector([selectConstants], (constants) => constants.initFinished || false);
export const selectOrderConstantsIsLoading = createSelector([selectConstantStatus], (status) => status === LoadingStatuses.pending || false);
export const selectOrderConstants = createSelector([selectConstants], (constants) => constants.orders || {});
export const selectOrderStatuses = createSelector([selectOrderConstants], (orderConstants) => orderConstants.statuses || {});
export const selectOrderCategories = createSelector([selectOrderConstants], (orderConstants) => orderConstants.categories || {});
export const selectHasOrderCategories = createSelector(
  [selectOrderCategories],
  (orderCategories) => orderCategories && Object.keys(orderCategories).length > 0
);
export const selectCustomers = createSelector([selectOrderConstants], (orderConstants) => orderConstants.customers || {});
export const selectRawCustomerList = createSelector([selectOrderConstants], (orderConstants) => orderConstants.customerIDNames || {});
export const selectProfileCustomers = createSelector([selectOrderConstants], (orderConstants) => orderConstants.profileCustomers || {});
export const selectCustomersList = createSelector(
  [selectProfileCustomers],
  (profileCustomers) => Object.keys(profileCustomers).map((customer) => customer) || []
);
export const selectVfsTaskTypes = createSelector([selectConstants], (constants) => constants.vfsTaskTypes || {});

export const selectLanguageConstants = createSelector([selectConstants], (constants) => {
  return constants.languages;
});
export const selectAllLanguages = createSelector([selectLanguageConstants], (languageConstants) => {
  const audio = languageConstants?.audio || [];
  const subtitle = languageConstants?.subtitles || [];
  const base = languageConstants?.base || [];
  const combined = [...audio, ...subtitle, ...base];
  const uniqueCombined = new Map();
  for (const item of combined) {
    if (!uniqueCombined.has(item.code)) {
      uniqueCombined.set(item.code, item);
    }
  }
  return Array.from(uniqueCombined.values());
});
export const selectAllLanguagesMap = createSelector([selectAllLanguages], (allLanguages: LanguageObject[]) => {
  if (!allLanguages?.length) return {};
  return allLanguages.reduce(
    (acc, lang) => {
      acc[lang.code] = lang;
      return acc;
    },
    {} as Record<string, LanguageObject>
  );
});
export const selectAudioLanguages = createSelector([selectLanguageConstants], (languageConstants) => languageConstants?.audio);
export const selectAudioLanguagesMap = createSelector([selectLanguageConstants], (languageConstants) => {
  if (!languageConstants?.audio) return {};
  return languageConstants.audio.reduce(
    (acc, lang) => {
      acc[lang.code] = lang;
      return acc;
    },
    {} as Record<string, LanguageObject>
  );
});
export const selectBaseLanguages = createSelector([selectLanguageConstants], (languageConstants) => languageConstants?.base);
export const selectSubtitlesLanguages = createSelector([selectLanguageConstants], (languageConstants) => languageConstants?.subtitles);
export const selectSubtitlesLanguagesMap = createSelector([selectLanguageConstants], (languageConstants) => {
  if (!languageConstants?.subtitles) return {};
  return languageConstants.subtitles.reduce(
    (acc, lang) => {
      acc[lang.code] = lang;
      return acc;
    },
    {} as Record<string, LanguageObject>
  );
});
export const selectLanguagesFilter = createSelector([selectAllLanguages], (languages) => {
  return languages.reduce((acc, lang) => {
    acc[lang.code] = lang.label;
    return acc;
  }, {});
});
export const selectShortListLanguagesFilter = createSelector([selectAllLanguages], (languages) => {
  return languages.reduce((acc, lang) => {
    if (!lang.is_short_list || lang.code === 'any' || lang.code === 'engcc') return acc;
    acc[lang.code] = lang.label;
    return acc;
  }, {});
});
export const selectFileTypeGroupings = createSelector(
  [selectConstants],
  (constants) =>
    constants?.commonConstants?.fileTypeGrouping ||
    ({
      audio: [],
      video: [],
      image: [],
      document: [],
      other: [],
      subtitle: [],
      captions: []
    } as FileTypeGrouping)
);
export const selectFileTypes = createSelector([selectConstants], (constants) => {
  const fileTypeGrouping: Record<AssetType, string[]> = constants.commonConstants?.fileTypeGrouping || ({} as Record<AssetType, string[]>);
  const fileTypes: FileTypes = {};

  for (const category in fileTypeGrouping) {
    for (const grouping of fileTypeGrouping[category as AssetType]) {
      fileTypes[grouping as keyof FileTypes] = category as AssetType;
    }
  }

  return fileTypes;
});

export const selectTaskQCTypes = createSelector([selectConstants], (constants) => constants.tasks?.qc_task_types || {});
export const selectTaskStatuses = createSelector([selectConstants], (constants) => constants.tasks?.statuses || {});
export const selectTaskTypes = createSelector([selectConstants], (constants) => constants.tasks?.types || {});
export const selectManualTaskNames = createSelector([selectConstants], (constants) => constants.tasks?.manualTaskNames || {});
export const selectTaskTypeGroups = createSelector([selectConstants], (constants) => constants.tasks?.taskTypeGroups || {});
export const selectNetworkCodes = createSelector([selectConstants], (constants) => constants.tasks?.networkCodes || {});
export const selectScreeningResults = createSelector([selectConstants], (constants) => constants.tasks?.task_qc_screening_results || {});

export const selectTitleTypes = createSelector([selectConstants], (constants) => constants.titles?.types || {});
export const selectEntityTypeLabels = createSelector([selectConstants], (constants) => constants.titles?.entityTypeLabels || {});
export const selectHierarchies = createSelector([selectConstants], (constants) => constants.titles?.hierarchies || {});
export const selectEntityStatuses = createSelector([selectConstants], (constants) => constants.titles?.entity_status || {});
export const selectJobHumanStatuses = createSelector([selectConstants], (constants) => constants.pendingAssets?.humanStatuses || {});
export const selectAssetComponentTypes = createSelector([selectConstants], (constants) => constants.pendingAssets?.componentTypes || {});
export const selectAssignableTaskGroups = createSelector([selectConstants], (constants) => constants.pendingAssets?.assignable_task_groups || {});

export const selectTransferQueueStatuses = createSelector([selectConstants], (constants) => constants.transferQueue?.statuses || {});
const selectCurrentTable = (state: RootState) => state.dataTable.currentTable;

export const selectStatuses = createSelector([selectCurrentTable, selectConstants], (currentTableKey, constants) => {
  if (!currentTableKey) return {};
  if (currentTableKey === 'jobDetailTasks') {
    return constants.jobs?.statuses || {};
  } else if (typeof currentTableKey === 'string' && currentTableKey.toLowerCase().includes('tasks')) {
    return constants.tasks?.statuses || {};
  } else {
    const currentTableConstants = constants[currentTableKey as keyof ConstantsState];
    if (typeof currentTableConstants === 'object' && currentTableConstants !== null && 'statuses' in currentTableConstants) {
      return currentTableConstants.statuses || {};
    }
    return {};
  }
});

export const selectStages = createSelector([selectCurrentTable, selectConstants], (currentTableKey, constants) => {
  if (!currentTableKey) return {};
  const currentTableConstants = constants[currentTableKey as keyof ConstantsState];
  if (currentTableKey === 'jobDetailTasks') {
    return constants.jobs?.stages || {};
  } else if (typeof currentTableConstants === 'object' && currentTableConstants !== null && 'stages' in currentTableConstants) {
    return currentTableConstants.stages || {};
  }
  return {};
});

export const selectSubStatuses = createSelector([selectCurrentTable, selectConstants], (currentTableKey, constants) => {
  if (!currentTableKey) return {};
  const currentTableConstants = constants[currentTableKey as keyof ConstantsState];
  if (typeof currentTableConstants === 'object' && currentTableConstants !== null && 'subStatuses' in currentTableConstants) {
    return currentTableConstants.subStatuses || {};
  }
  return {};
});

export const selectJobPriorityLevels = createSelector([selectConstants], (constants) => constants.jobs?.job_priority_levels || {});
export const selectJobStages = createSelector([selectConstants], (constants) => constants.jobs?.stages || {});
export const selectJobStatuses = createSelector([selectConstants], (constants) => constants.jobs?.statuses || {});
export const selectJobTypes = createSelector([selectConstants], (constants) => constants.jobs?.types || {});
export const selectJobWorkflows = createSelector([selectConstants], (constants) => constants.jobs?.jobWorkflows || {});
export const selectPlaybackUrl = createSelector([selectConstants], (constants) => constants.vfs_playback_base_url || null);

export const selectUserEmails = (state: RootState) => selectConstants(state).userEmails;

export const selectPendingAssetSystemStatuses = createSelector([selectConstants], (constants) => constants.pendingAssets?.systemStatuses || {});
export const selectMediaResolutions = createSelector([selectConstants], (constants) => {
  if (!constants?.media_resolutions || constants.media_resolutions.length <= 0) {
    return MediaResolutionsList;
  }

  return constants.media_resolutions;
});

export const selectExceptionTypes = createSelector([selectConstants], (constants) => constants.exceptions?.types || {});
export const selectExceptionSubtypes = createSelector([selectConstants], (constants) => constants.exceptions?.subtypes || {});
export const selectExceptionReferenceTypes = createSelector([selectConstants], (constants) => constants.exceptions?.referenceTypes || {});
export const selectExceptionSeverities = createSelector([selectConstants], (constants) => constants.exceptions?.severities || {});

export const selectAvailTerritory = createSelector([selectConstants], (constants) => constants.avails?.localeTerritory || {});
export const selectAvailLicense = createSelector([selectConstants], (constants) => constants.avails?.license || {});
export const selectAvailPlatform = createSelector([selectConstants], (constants) => constants.avails?.availedPlatform || {});
export const selectAvailScheduleStatuses = createSelector([selectConstants], (constants) => constants.avails?.scheduleStatus || {});
export const selectAvailPlatformIds = createSelector([selectConstants], (constants) => constants.avails?.platformIds || {});

export const selectFileTypeGroupingsFromApi = createSelector([selectConstants], (constants) => constants.fileTypeGroupingsFromApi);

export const selectBaseTerritoryGroupings = createSelector([selectConstants], (constants) => {
  return constants.territoryGroupings || {};
});
export const selectFlatTerritoryGroupingsWithRegion = createSelector([selectBaseTerritoryGroupings], (groupings) => {
  const territories: (TerritoryConstant & { region: string })[] = [];
  Object.keys(groupings).forEach((groupingKey) => {
    const grouping = groupings[groupingKey];
    const groupingWithRegion = grouping.map((territory) => ({ ...territory, region: groupingKey }));
    territories.push(...groupingWithRegion);
  });
  return territories;
});
export const selectGeoTerritoryGroupings = createSelector([selectConstants], (constants) => constants.geoTerritoryGroupings || {});
export const selectTimezoneTerritoryGroupings = createSelector([selectConstants], (constants) => constants.timezoneTerritoryGroupings || {});

export const selectCutTypeLabels = createSelector([selectConstants], (constants) => constants.packaging?.cutTypeLabels || {});
